/**
 * Contact page content — 100% fidelity to content/company/contact.md
 * Cross-reference: content/company/contact.md
 * 18 sections, all handcrafted from source.
 */

export const contactMeta = {
  title: "Contact | Perception X2 Get in Touch",
  description:
    "Connect with Perception X2. Request a demo, book a consultation, or speak with our team. Dedicated lines for sales, partnerships, media, and support across global offices.",
  slug: "contact",
  canonical: "https://perception.ac/contact",
};

export interface HeroData {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  body: string;
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const hero: HeroData = {
  eyebrow: "Contact Perception X2",
  title: "Connect with Perception X2.",
  titleAccent: "Let's Build Your Perception Strategy.",
  lede: "Perception X2 operates dedicated inquiry channels for every engagement type — from enterprise sales and strategic partnerships to media relations and technical support. Our global team responds within guaranteed timeframes, ensuring your inquiry reaches the right specialist immediately.",
  body: "Whether you are exploring autonomous perception amplification for the first time or scaling an existing partnership, Perception X2 provides direct access to senior specialists who understand your objectives. Every inquiry receives executive-level attention. Every consultation remains under strict NDA protection.",
  cta: [
    { label: "Request a Demo", href: "#demo-request" },
    { label: "Book a Consultation", href: "#consultation-booking" },
    { label: "Sales Inquiry", href: "#sales-inquiries" },
  ],
};

export interface ExecSummaryData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const executiveSummary: ExecSummaryData = {
  eyebrow: "Executive Summary",
  title: "The Perception X2 Engagement Framework",
  intro: "Perception X2 maintains a structured engagement framework designed to connect you with the right team, at the right time, for the right outcome. Our contact infrastructure is not a generic inbox — it is an intelligent routing system that directs inquiries to senior specialists based on inquiry type, organizational profile, and engagement urgency.",
  pillars: [
    {
      title: "How Our Contact System Works",
      body: "Every inquiry submitted through Perception X2 undergoes intelligent routing. The system evaluates your organization profile, inquiry category, and urgency indicators to connect you with a specialist who has direct authority to address your needs. No ticket queues. No generic responses. Direct access to decision-makers.",
    },
    {
      title: "Dedicated Channels for Every Need",
      body: "Perception X2 operates separate inquiry channels for enterprise sales, strategic partnerships, media relations, technical support, careers, and general inquiries. Each channel staffs senior specialists with domain expertise, ensuring your inquiry receives expert attention from first contact.",
    },
    {
      title: "Guaranteed Response Times",
      body: "We commit to response time SLAs for every inquiry channel. Sales inquiries receive initial response within 4 business hours. Support inquiries within 2 business hours. Partnership inquiries within 8 business hours. These are not aspirational targets — they are contractual commitments backed by operational infrastructure.",
    },
    {
      title: "Global Accessibility",
      body: "With offices across multiple time zones, Perception X2 ensures your inquiry reaches a live specialist during your business hours. Our global coordination infrastructure enables seamless handoffs between regional teams, maintaining continuity across time zones and engagement types.",
    },
  ],
  cta: [
    { label: "Submit an Inquiry", href: "#contact-form" },
    { label: "View Office Locations", href: "#office-locations" },
  ],
};

export interface ContactFormData {
  eyebrow: string;
  title: string;
  intro: string;
  categories: { name: string; body: string }[];
  required: string[];
  process: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const contactForm: ContactFormData = {
  eyebrow: "Contact Form",
  title: "Submit Your Inquiry Directly",
  intro: "The Perception X2 contact form routes your inquiry to the appropriate specialist team based on your responses. Complete the form below and receive a guaranteed response within your inquiry category's SLA.",
  categories: [
    { name: "Enterprise Sales", body: "Pricing, platform capabilities, custom deployment, procurement process" },
    { name: "Strategic Partnerships", body: "Technology integrations, co-development, channel partnerships" },
    { name: "Media and Press", body: "Press inquiries, interviews, brand assets, media kits" },
    { name: "Technical Support", body: "Platform issues, integration assistance, troubleshooting, account access" },
    { name: "Careers", body: "Open positions, application inquiries, recruitment partnerships" },
    { name: "General Inquiries", body: "Information requests, event inquiries, research collaborations" },
  ],
  required: [
    "Full name and professional title",
    "Organization name and website",
    "Email address and phone number",
    "Inquiry category selection",
    "Brief description of your objective or question",
  ],
  process: [
    "Upon form submission, our intelligent routing system assigns your inquiry to a senior specialist within your category.",
    "You receive an acknowledgment email within minutes, followed by a substantive response within your category's SLA.",
    "For urgent matters, our form includes an escalation option that triggers immediate specialist outreach.",
  ],
  cta: [
    { label: "Submit Inquiry", href: "#contact-form" },
    { label: "Book a Consultation Instead", href: "#consultation-booking" },
  ],
};

export interface OfficeData {
  eyebrow: string;
  title: string;
  intro: string;
  hq: { label: string; body: string }[];
  regional: { label: string; body: string }[];
  virtual: { title: string; body: string };
  visit: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const offices: OfficeData = {
  eyebrow: "Office Locations",
  title: "Global Presence, Local Expertise",
  intro: "Perception X2 maintains offices across strategic global locations, each staffed with senior specialists who understand regional market dynamics, regulatory environments, and cultural nuances that influence perception strategy.",
  hq: [
    { label: "Primary Office", body: "Executive leadership, platform development, and enterprise sales" },
    { label: "Hours", body: "Monday through Friday, 09:00 to 18:00 local time" },
    { label: "Phone", body: "Available for scheduled consultations and urgent inquiries" },
    { label: "Walk-Ins", body: "By appointment only; scheduled through the contact form" },
  ],
  regional: [
    { label: "Regional Hub 1", body: "Enterprise sales and client success for the surrounding region" },
    { label: "Regional Hub 2", body: "Partnership development and media relations" },
    { label: "Regional Hub 3", body: "Technical support and implementation services" },
  ],
  virtual: {
    title: "Virtual Operations",
    body: "Perception X2 also maintains fully operational virtual offices, enabling our specialists to serve clients regardless of geographic proximity. Virtual consultations, remote demonstrations, and digital workshops deliver the same depth of engagement as in-person meetings.",
  },
  visit: {
    title: "Visiting Our Offices",
    body: "All office visits require advance scheduling through our contact form. This ensures the right specialist is available for your meeting and that any required security protocols are pre-arranged. Our offices maintain strict confidentiality standards, and all visitor access is logged and escorted.",
  },
  cta: [
    { label: "Schedule a Visit", href: "#contact-form" },
    { label: "Request Virtual Consultation", href: "#consultation-booking" },
  ],
};

export interface GeneralInqData {
  eyebrow: string;
  title: string;
  intro: string;
  coverage: string[];
  reach: { title: string; body: string }[];
  expect: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const generalInquiries: GeneralInqData = {
  eyebrow: "General Inquiries",
  title: "Information Requests and Exploratory Conversations",
  intro: "For organizations exploring autonomous perception amplification for the first time, our general inquiries channel provides access to senior strategists who can discuss your objectives, assess feasibility, and recommend next steps — without commitment or pressure.",
  coverage: [
    "Platform overview and capability demonstrations",
    "Industry-specific application discussions",
    "Competitive comparison consultations",
    "Regulatory and compliance questions",
    "Event and conference inquiries",
    "Research collaboration proposals",
  ],
  reach: [
    { title: "How to Reach Us", body: "General inquiries receive initial response within 12 business hours. For faster engagement, use the contact form with \"General Inquiry\" selected, or email our general inquiries team directly at the address provided on submission confirmation." },
  ],
  expect: {
    title: "What to Expect",
    body: "A senior strategist will review your inquiry and schedule a 30-minute exploratory call at your convenience. This call covers your objectives, assesses fit, and provides a clear recommendation for next steps — whether that is a formal demo, a deeper consultation, or simply providing the information you need to make an informed decision.",
  },
  cta: [
    { label: "Submit General Inquiry", href: "#contact-form" },
    { label: "Request a Demo", href: "#demo-request" },
  ],
};

export interface SalesInqData {
  eyebrow: string;
  title: string;
  intro: string;
  topics: string[];
  process: { step: string; title: string; body: string }[];
  pricing: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const salesInquiries: SalesInqData = {
  eyebrow: "Sales Inquiries",
  title: "Enterprise Sales and Custom Deployment",
  intro: "Perception X2 enterprise sales engagements are led by senior account executives with deep platform expertise and direct authority over pricing, deployment scope, and custom configuration. Every sales inquiry receives executive-level attention from first contact.",
  topics: [
    "Platform pricing and licensing models",
    "Custom deployment architectures",
    "Enterprise integration requirements",
    "Procurement and legal review processes",
    "ROI modeling and business case development",
    "Pilot program structures",
    "Volume and multi-year agreements",
  ],
  process: [
    { step: "1", title: "Inquiry Submission", body: "Complete the contact form with \"Enterprise Sales\" selected" },
    { step: "2", title: "Specialist Assignment", body: "A senior account executive reviews your inquiry within 4 business hours" },
    { step: "3", title: "Discovery Call", body: "45-minute session to understand your objectives, challenges, and requirements" },
    { step: "4", title: "Custom Proposal", body: "Tailored proposal with pricing, deployment timeline, and success metrics" },
    { step: "5", title: "Executive Review", body: "Optional executive briefing for senior stakeholders" },
    { step: "6", title: "Pilot or Deployment", body: "Structured implementation with dedicated success management" },
  ],
  pricing: {
    title: "Pricing Transparency",
    body: "Perception X2 does not publish fixed pricing because every deployment is configured to organizational requirements. However, our sales team provides transparent pricing discussions from the first conversation, including licensing models, implementation costs, and ongoing support fees. No hidden costs. No surprise invoices.",
  },
  cta: [
    { label: "Submit Sales Inquiry", href: "#contact-form" },
    { label: "Book a Demo", href: "#demo-request" },
  ],
};

export interface PartnerInqData {
  eyebrow: string;
  title: string;
  intro: string;
  categories: { name: string; body: string }[];
  criteria: { step: string; title: string; body: string }[];
  process: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const partnerInquiries: PartnerInqData = {
  eyebrow: "Partnership Inquiries",
  title: "Strategic Alliances and Technology Integrations",
  intro: "Perception X2 maintains an active partnership ecosystem spanning technology integrations, channel partnerships, co-development agreements, and strategic alliances. Our partnership team evaluates opportunities based on mutual value creation, market expansion potential, and technical compatibility.",
  categories: [
    { name: "Technology Partners", body: "API integrations, platform interoperability, data exchange protocols" },
    { name: "Channel Partners", body: "Reseller agreements, referral programs, co-marketing initiatives" },
    { name: "Strategic Alliances", body: "Joint go-to-market strategies, co-development projects, market expansion" },
    { name: "Research Partners", body: "Academic collaborations, industry research, white paper co-authorship" },
    { name: "Consulting Partners", body: "Implementation support, customization services, training delivery" },
  ],
  criteria: [
    { step: "1", title: "Strategic Alignment", body: "Does the partnership advance mutual market objectives?" },
    { step: "2", title: "Technical Compatibility", body: "Can our platforms integrate without compromising performance or security?" },
    { step: "3", title: "Value Creation", body: "Does the partnership generate measurable value for both organizations and shared clients?" },
  ],
  process: {
    title: "How to Propose a Partnership",
    body: "Submit a partnership inquiry through the contact form with \"Strategic Partnership\" selected. Include your organization profile, proposed partnership structure, and expected mutual benefits. Our partnership team reviews submissions within 8 business hours and schedules an exploratory discussion if alignment exists.",
  },
  cta: [
    { label: "Submit Partnership Inquiry", href: "#contact-form" },
    { label: "View Integration Partners", href: "/partners" },
  ],
};

export interface MediaInqData {
  eyebrow: string;
  title: string;
  intro: string;
  topics: string[];
  spokespersons: string[];
  resources: string[];
  embargo: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const mediaInquiries: MediaInqData = {
  eyebrow: "Media and Press Inquiries",
  title: "Press Relations, Interviews, and Brand Resources",
  intro: "Perception X2 maintains dedicated media relations infrastructure for press inquiries, interview requests, commentary opportunities, and brand asset distribution. Our media team responds within 6 business hours for time-sensitive requests.",
  topics: [
    "Executive interview requests",
    "Expert commentary on perception management, AI, and digital intelligence",
    "Press releases and announcement distribution",
    "Brand asset requests (logos, imagery, executive headshots)",
    "Conference and event media coordination",
    "Case study and research publication requests",
  ],
  spokespersons: [
    "Platform capability and technology discussions",
    "Industry trend analysis and market commentary",
    "AI ethics and responsible technology perspectives",
    "Enterprise perception management strategy",
  ],
  resources: [
    "Press kit with approved brand assets",
    "Executive biography sheets",
    "Company fact sheet and statistics",
    "B-roll and broadcast-ready footage",
    "Pre-approved quotes and talking points",
  ],
  embargo: {
    title: "Embargo and Exclusive Arrangements",
    body: "For embargoed announcements or exclusive coverage arrangements, contact our media team directly through the contact form with \"Media Inquiry\" selected and indicate the embargo timeline in your message.",
  },
  cta: [
    { label: "Submit Media Inquiry", href: "#contact-form" },
    { label: "Download Brand Assets", href: "/press" },
  ],
};

export interface SupportInqData {
  eyebrow: string;
  title: string;
  intro: string;
  tiers: { tier: string; body: string }[];
  topics: string[];
  access: { title: string; body: string };
  selfService: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const techSupport: SupportInqData = {
  eyebrow: "Technical Support",
  title: "Platform Support and Troubleshooting",
  intro: "Perception X2 technical support provides rapid resolution for platform issues, integration assistance, configuration questions, and account management. Our support engineers hold deep platform expertise and direct access to engineering resources.",
  tiers: [
    { tier: "Standard Support", body: "Email-based assistance with 24-hour response SLA" },
    { tier: "Priority Support", body: "Dedicated support engineer with 4-hour response SLA" },
    { tier: "Enterprise Support", body: "24/7 access to senior engineers with 2-hour response SLA" },
    { tier: "Critical Support", body: "Immediate escalation for platform-down or security incidents" },
  ],
  topics: [
    "Platform access and authentication issues",
    "Integration configuration and troubleshooting",
    "API endpoint assistance",
    "Dashboard and reporting questions",
    "Data export and analysis support",
    "Account management and billing inquiries",
  ],
  access: {
    title: "How to Access Support",
    body: "For fastest resolution, submit a support inquiry through the contact form with \"Technical Support\" selected. Include your account identifier, platform version, and a description of the issue. Our intelligent routing system assigns your inquiry to the appropriate support tier based on your service level and issue severity.",
  },
  selfService: [
    "Platform documentation and knowledge base",
    "Integration guides and API reference",
    "Video tutorials and walkthrough libraries",
    "Community forums and peer discussions",
  ],
  cta: [
    { label: "Submit Support Request", href: "#contact-form" },
    { label: "Access Documentation", href: "/resources" },
  ],
};

export interface CareersInqData {
  eyebrow: string;
  title: string;
  intro: string;
  roles: { area: string; focus: string }[];
  whatWeLookFor: string;
  process: { step: string; title: string; body: string }[];
  internships: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const careersContact: CareersInqData = {
  eyebrow: "Careers",
  title: "Join the Perception X2 Team",
  intro: "Perception X2 recruits exceptional talent across engineering, data science, operations, and client success. Our careers channel connects you with our talent acquisition team and provides visibility into current openings, team culture, and growth opportunities.",
  roles: [
    { area: "Engineering", focus: "Platform development, AI/ML research, infrastructure engineering" },
    { area: "Data Science", focus: "Analytics, modeling, predictive intelligence" },
    { area: "Operations", focus: "Client success, project management, quality assurance" },
    { area: "Sales and Marketing", focus: "Enterprise sales, partnerships, content strategy" },
    { area: "Corporate", focus: "Finance, legal, human resources, administrative" },
  ],
  whatWeLookFor: "Perception X2 values intellectual curiosity, technical excellence, and commitment to client outcomes. We seek candidates who thrive in fast-paced environments, communicate clearly, and approach complex problems with creative rigor.",
  process: [
    { step: "1", title: "Submit your inquiry", body: "through the contact form with \"Careers\" selected" },
    { step: "2", title: "Talent acquisition review", body: "Our talent acquisition team reviews your profile within 5 business days" },
    { step: "3", title: "Initial screening call", body: "with a talent specialist" },
    { step: "4", title: "Technical or domain assessment", body: "(role-dependent)" },
    { step: "5", title: "Team interviews and culture alignment", body: "" },
    { step: "6", title: "Offer and onboarding", body: "" },
  ],
  internships: {
    title: "Internships and Early Careers",
    body: "Perception X2 offers structured internship programs for university students and early-career professionals. These programs provide hands-on experience with enterprise-scale perception management platforms and direct mentorship from senior team members.",
  },
  cta: [
    { label: "Submit Careers Inquiry", href: "#contact-form" },
    { label: "View Open Positions", href: "/careers" },
  ],
};

export interface DemoReqData {
  eyebrow: string;
  title: string;
  intro: string;
  format: { label: string; value: string }[];
  coverage: { step: string; title: string; body: string }[];
  request: { title: string; body: string };
  briefing: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const demoRequests: DemoReqData = {
  eyebrow: "Demo Requests",
  title: "Experience Perception X2 in Action",
  intro: "Perception X2 demos provide a comprehensive platform walkthrough tailored to your industry, use case, and organizational objectives. Our demos are led by senior product specialists who demonstrate real capabilities — not marketing abstractions.",
  format: [
    { label: "Duration", value: "45 to 60 minutes" },
    { label: "Format", value: "Live platform demonstration via video conference" },
    { label: "Customization", value: "Tailored to your industry and specific use case" },
    { label: "Attendees", value: "Up to 10 participants from your organization" },
    { label: "Follow-Up", value: "Custom proposal and next-steps discussion within 24 hours" },
  ],
  coverage: [
    { step: "1", title: "Platform Overview", body: "Architecture, capabilities, and integration ecosystem" },
    { step: "2", title: "Live Demonstration", body: "Real-time perception amplification across sample platforms" },
    { step: "3", title: "Use Case Deep Dive", body: "Your specific scenario addressed with platform capabilities" },
    { step: "4", title: "Analytics Walkthrough", body: "Dashboard, metrics, and performance tracking" },
    { step: "5", title: "Security and Compliance", body: "Data protection, access controls, and regulatory adherence" },
    { step: "6", title: "Pricing Discussion", body: "Transparent overview of licensing and deployment models" },
  ],
  request: {
    title: "How to Request a Demo",
    body: "Complete the contact form with \"Demo Request\" selected. Include your industry, organizational size, and primary objectives so our team can prepare a customized demonstration that addresses your specific needs.",
  },
  briefing: {
    title: "Executive Briefings",
    body: "For C-suite and board-level stakeholders, Perception X2 offers executive briefing sessions that focus on strategic implications, competitive positioning, and organizational impact rather than platform mechanics.",
  },
  cta: [
    { label: "Request a Demo", href: "#contact-form" },
    { label: "Book an Executive Briefing", href: "#consultation-booking" },
  ],
};

export interface ConsultData {
  eyebrow: string;
  title: string;
  intro: string;
  types: { name: string; body: string }[];
  process: { step: string; title: string; body: string }[];
  confidentiality: { title: string; body: string };
  post: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const consultation: ConsultData = {
  eyebrow: "Consultation Booking",
  title: "Schedule a Strategic Session with Perception X2",
  intro: "Perception X2 consultations provide dedicated time with senior strategists to discuss your perception management objectives, assess organizational readiness, and develop a preliminary strategy framework — before any commercial commitment.",
  types: [
    { name: "Strategy Consultation", body: "(60 minutes) — Discuss your perception objectives, competitive landscape, and strategic approach with a senior strategist" },
    { name: "Technical Consultation", body: "(45 minutes) — Address integration architecture, data requirements, and technical feasibility with a solutions architect" },
    { name: "Executive Briefing", body: "(30 minutes) — High-level overview for C-suite stakeholders focused on business impact and ROI" },
    { name: "Workshop Facilitation", body: "(Half-day) — Structured session with your team to map perception challenges and define success criteria" },
  ],
  process: [
    { step: "1", title: "Select your consultation type", body: "through the contact form with \"Consultation Booking\" selected" },
    { step: "2", title: "Provide your preferred date, time, and timezone", body: "" },
    { step: "3", title: "Receive confirmation within 4 business hours with meeting details", body: "" },
    { step: "4", title: "Pre-consultation questionnaire", body: "ensures productive use of your time" },
    { step: "5", title: "Consultation conducted", body: "via video conference or in-person at your preferred office" },
  ],
  confidentiality: {
    title: "Consultation Confidentiality",
    body: "All consultations operate under strict NDA protection. Discussion content, strategic insights, and organizational details remain confidential and are not used for any purpose beyond the consultation itself.",
  },
  post: {
    title: "Post-Consultation",
    body: "Following your consultation, you receive a summary document with key insights, recommended next steps, and relevant resources. If you choose to proceed, your consultant facilitates introduction to the appropriate sales or implementation team.",
  },
  cta: [
    { label: "Book a Consultation", href: "#contact-form" },
    { label: "Request a Demo Instead", href: "#demo-request" },
  ],
};

export interface TrustData {
  eyebrow: string;
  title: string;
  intro: string;
  dataProtection: { label: string; body: string }[];
  confidentiality: string[];
  secureComm: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const trust: TrustData = {
  eyebrow: "Trust and Security",
  title: "Your Data and Discussions Are Protected",
  intro: "Perception X2 maintains rigorous security protocols across all communication channels, ensuring your inquiries, consultations, and organizational details remain confidential and protected.",
  dataProtection: [
    { label: "Encryption", body: "All form submissions and communications encrypted in transit and at rest" },
    { label: "Access Controls", body: "Role-based access ensures only authorized personnel view your inquiry" },
    { label: "Data Retention", body: "Inquiry data retained only as long as necessary for engagement purposes" },
    { label: "Compliance", body: "Adherence to GDPR, CCPA, and applicable regional data protection regulations" },
  ],
  confidentiality: [
    "All consultations and discussions operate under NDA protection by default",
    "Custom NDA agreements available upon request for enterprise engagements",
    "Internal access logging ensures complete audit trail of inquiry handling",
    "No inquiry data shared with third parties without explicit written consent",
  ],
  secureComm: [
    "Encrypted email for sensitive correspondence",
    "Secure video conferencing for virtual meetings",
    "Encrypted file transfer for document exchange",
    "Two-factor authentication for platform access during demos",
  ],
  cta: [
    { label: "Review Our Security Practices", href: "/about#technology" },
    { label: "Request Custom NDA", href: "#contact-form" },
  ],
};

export interface ResponseData {
  eyebrow: string;
  title: string;
  intro: string;
  rows: { type: string; initial: string; resolution: string }[];
  businessHours: { title: string; body: string };
  escalation: { step: string; title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const responseTimes: ResponseData = {
  eyebrow: "Response Times",
  title: "Guaranteed Response SLAs by Inquiry Type",
  intro: "Perception X2 commits to response time SLAs for every inquiry channel. These are contractual commitments backed by operational infrastructure and monitored through real-time dashboards.",
  rows: [
    { type: "Enterprise Sales", initial: "4 business hours", resolution: "5 business days" },
    { type: "Technical Support (Standard)", initial: "24 business hours", resolution: "3 business days" },
    { type: "Technical Support (Priority)", initial: "4 business hours", resolution: "1 business day" },
    { type: "Technical Support (Enterprise)", initial: "2 business hours", resolution: "4 business hours" },
    { type: "Technical Support (Critical)", initial: "30 minutes", resolution: "2 business hours" },
    { type: "Strategic Partnership", initial: "8 business hours", resolution: "10 business days" },
    { type: "Media and Press", initial: "6 business hours", resolution: "2 business days" },
    { type: "Careers", initial: "5 business days", resolution: "15 business days" },
    { type: "General Inquiries", initial: "12 business hours", resolution: "5 business days" },
    { type: "Demo Request", initial: "4 business hours", resolution: "3 business days" },
    { type: "Consultation Booking", initial: "4 business hours", resolution: "2 business days" },
  ],
  businessHours: {
    title: "Business Hours",
    body: "Response SLAs are measured in business hours (Monday through Friday, 09:00 to 18:00 in the respondent's local timezone). Critical support inquiries are measured in calendar hours due to their urgency.",
  },
  escalation: [
    { step: "1", title: "First Escalation", body: "Supervisor notification at SLA + 50%" },
    { step: "2", title: "Second Escalation", body: "Director notification at SLA + 100%" },
    { step: "3", title: "Executive Escalation", body: "VP notification at SLA + 150%" },
  ],
  cta: [
    { label: "Submit an Inquiry", href: "#contact-form" },
    { label: "Check Response SLAs", href: "#response-times" },
  ],
};

export interface ChannelsData {
  eyebrow: string;
  title: string;
  intro: string;
  primary: { label: string; body: string }[];
  digital: string[];
  events: string[];
  guidance: { label: string; channel: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const channels: ChannelsData = {
  eyebrow: "Communication Channels",
  title: "Multiple Ways to Reach Perception X2",
  intro: "Perception X2 maintains communication channels across multiple platforms and formats, ensuring you can reach us through the method most convenient for your situation.",
  primary: [
    { label: "Contact Form", body: "Intelligent routing to the right specialist; fastest resolution for structured inquiries" },
    { label: "Email", body: "Dedicated email addresses for each inquiry category; detailed correspondence with attachments" },
    { label: "Phone", body: "Scheduled consultations and urgent inquiries; available during business hours across time zones" },
    { label: "Video Conference", body: "Demos, consultations, and meetings via secure video platforms" },
  ],
  digital: [
    "LinkedIn — Corporate updates, thought leadership, and direct messaging for professional inquiries",
    "Twitter/X — Real-time updates, announcements, and public engagement",
    "YouTube — Platform demos, webinars, and educational content",
    "Podcast — Long-form discussions on perception management, AI, and digital intelligence",
  ],
  events: [
    "Conferences — Meet Perception X2 at industry events throughout the year",
    "Webinars — Live and on-demand educational sessions on platform capabilities",
    "Workshops — Structured learning sessions for teams exploring perception management",
  ],
  guidance: [
    { label: "Urgent matters", channel: "Phone or Critical Support form submission" },
    { label: "Detailed discussions", channel: "Video consultation or phone call" },
    { label: "Documentation exchange", channel: "Email with encrypted attachments" },
    { label: "Exploratory conversations", channel: "Contact form or LinkedIn message" },
    { label: "Media requests", channel: "Dedicated media inquiry form" },
  ],
  cta: [
    { label: "Choose Your Channel", href: "#contact-form" },
    { label: "View Upcoming Events", href: "/press" },
  ],
};

export interface WhyContactData {
  eyebrow: string;
  title: string;
  intro: string;
  whatDistinguishes: { title: string; body: string }[];
  clientOutcomes: string[];
  difference: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const whyContact: WhyContactData = {
  eyebrow: "Why Contact Perception X2",
  title: "The Value of Engaging Our Team",
  intro: "Contacting Perception X2 connects you with 15+ years of perception management expertise, access to nine proprietary AI platforms, and a team that has delivered results for 300+ elite clients across government, enterprise, and institutional sectors.",
  whatDistinguishes: [
    { title: "Senior-Level Attention", body: "Every inquiry handled by specialists with decision-making authority, not junior representatives reading scripts" },
    { title: "No-Pressure Consultations", body: "Exploratory conversations focused on your objectives, not sales tactics" },
    { title: "Transparent Pricing", body: "Honest discussions about costs, timelines, and expected outcomes from first contact" },
    { title: "Custom Solutions", body: "Every engagement tailored to your specific industry, objectives, and organizational context" },
    { title: "Measurable Outcomes", body: "Clear success metrics and accountability frameworks established before engagement begins" },
  ],
  clientOutcomes: [
    "300-500% improvement in narrative penetration",
    "95-98% organic authenticity across deployed content",
    "60-80% reduction in crisis response activation time",
    "50-70% decrease in negative sentiment propagation",
    "40-60% improvement in search result ownership",
  ],
  difference: {
    title: "The Perception X2 Difference",
    body: "When you contact Perception X2, you are not submitting a ticket into a queue. You are initiating a conversation with a team that has spent 15+ years refining the science and art of perception management. Our specialists have personally overseen 300+ enterprise engagements and carry institutional knowledge that no documentation or automation can replace.",
  },
  cta: [
    { label: "Contact Us Now", href: "#contact-form" },
    { label: "Request a Demo", href: "#demo-request" },
  ],
};

export interface FaqData {
  eyebrow: string;
  title: string;
  items: { q: string; a: string }[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const contactFaq: FaqData = {
  eyebrow: "FAQ",
  title: "Contacting Perception X2: Common Questions",
  items: [
    { q: "How quickly will I receive a response after submitting an inquiry?", a: "Response times vary by inquiry category. Enterprise sales inquiries receive initial response within 4 business hours. Technical support inquiries within 2 business hours for priority clients. General inquiries within 12 business hours. All response times are contractual SLAs, not aspirational targets." },
    { q: "Can I speak directly with a specialist instead of submitting a form?", a: "Yes. While the contact form ensures intelligent routing to the right specialist, you may also request a direct call during form submission. Indicate your preferred contact method and time, and a specialist will reach out to you directly." },
    { q: "Is my inquiry confidential?", a: "Absolutely. All inquiries, consultations, and communications operate under strict confidentiality protocols. Custom NDA agreements are available for enterprise engagements. No inquiry data is shared externally without explicit written consent." },
    { q: "What information should I include in my inquiry for fastest response?", a: "Include your name, title, organization, inquiry category, and a brief description of your objective or question. The more context you provide, the faster our routing system can assign the right specialist and prepare for your engagement." },
    { q: "Do you offer consultations before any commercial commitment?", a: "Yes. Perception X2 offers complimentary strategy consultations, technical consultations, and executive briefings. These sessions provide genuine value regardless of whether you proceed with a commercial engagement." },
    { q: "Can I schedule a demo for my team?", a: "Yes. Demos accommodate up to 10 participants and are customized to your industry and use case. Request a demo through the contact form and our team will prepare a tailored demonstration within 3 business days." },
    { q: "What security measures protect my communication with Perception X2?", a: "All communications encrypted in transit and at rest. Form submissions, emails, and video conferences use enterprise-grade encryption. Platform access during demos requires two-factor authentication. All data handling complies with GDPR, CCPA, and applicable regional regulations." },
    { q: "How do I escalate an urgent matter?", a: "For critical matters requiring immediate attention, use the contact form with \"Critical Support\" selected or call our priority line. Critical inquiries trigger automatic escalation protocols and receive initial response within 30 minutes." },
    { q: "Do you have offices I can visit in person?", a: "Yes. Perception X2 maintains offices across multiple global locations. All visits require advance scheduling through the contact form to ensure the right specialist is available and security protocols are arranged." },
    { q: "What happens after my initial consultation?", a: "Following your consultation, you receive a summary document with key insights, recommended next steps, and relevant resources. If you choose to proceed, your consultant facilitates introduction to the appropriate sales, implementation, or support team." },
  ],
};

export interface CtaData {
  eyebrow: string;
  title: string;
  intro: string;
  confidentiality: string;
  cta: { label: string; href: string }[];
  trustBar: string[];
  pillars?: { title: string; body: string }[];
}

export const contactCta: CtaData = {
  eyebrow: "Get Started",
  title: "Ready to Transform Your Perception Strategy?",
  intro: "Every perception challenge has a solution. Every solution begins with a conversation. Contact Perception X2 today and discover how autonomous perception amplification can reshape your digital presence with 300-500% improvement in narrative penetration and 95-98% organic authenticity.",
  confidentiality: "Confidentiality Assured. All inquiries, consultations, and communications remain under strict NDA protection. Your strategic objectives are your competitive advantage — we protect them accordingly.",
  cta: [
    { label: "Request a Demo", href: "#demo-request" },
    { label: "Book a Consultation", href: "#consultation-booking" },
    { label: "Submit an Inquiry", href: "#contact-form" },
  ],
  trustBar: ["15+ Years", "300+ Elite Clients", "9 Proprietary AI Platforms", "99.9999% Uptime"],
};
